import { useMemo, useState } from "react";
import { Plus, Minus, Pencil, Trash2, X, Check, Loader2, ChefHat } from "lucide-react";
import { api, money, errorMessage } from "../api";

const UNITS = ["g","kg","ml","L","pcs","pkts","box"];
const CATEGORIES = ["Starters","Mains","Desserts","Beverages","Breads","Sides","Combos"];
const GST_RATES = [0, 5, 12, 18, 28];

function RecipeForm({ initial, inventory, onCancel, onSave, busy, error }) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    category: initial?.category || "Mains",
    yield_servings: initial?.yield_servings || 1,
    selling_price: initial?.selling_price || 0,
    wastage: initial?.wastage ?? 3,
    gst_rate: initial?.gst_rate ?? 5,
    hsn_sac: initial?.hsn_sac || "2106",
    notes: initial?.notes || "",
    ingredients: initial?.ingredients || [],
  }));

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addIng = () => setForm(f => ({ ...f, ingredients: [...f.ingredients, { ingredient_id: "", name: "", unit: "g", qty: 0, unit_cost: 0 }] }));
  const updIng = (i, patch) => setForm(f => ({ ...f, ingredients: f.ingredients.map((x, idx) => idx === i ? { ...x, ...patch } : x) }));
  const rmIng = (i) => setForm(f => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }));

  const pickIngredient = (i, ingredient_id) => {
    const inv = inventory.find(x => x.id === ingredient_id);
    if (!inv) return updIng(i, { ingredient_id: "" });
    // Convert master unit cost (per unit) to per-sub-unit if needed
    // Simple approach: use ingredient as-is; cost = unit_cost / 1000 if unit = kg->g, etc. For MVP, keep master unit.
    updIng(i, { ingredient_id: inv.id, name: inv.name, unit: inv.unit, unit_cost: inv.unit_cost });
  };

  const subtotal = form.ingredients.reduce((a, b) => a + (Number(b.qty) || 0) * (Number(b.unit_cost) || 0), 0);
  const costPerBatch = subtotal * (1 + (Number(form.wastage) || 0) / 100);
  const costPerServing = costPerBatch / Math.max(1, Number(form.yield_servings) || 1);
  const foodCostPct = form.selling_price > 0 ? (costPerServing / Number(form.selling_price)) * 100 : 0;

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <div className="form-grid">
        <label>Dish name<input value={form.name} onChange={e => upd("name", e.target.value)} required placeholder="e.g. Paneer Butter Masala" data-testid="recipe-name-input"/></label>
        <label>Category<select value={form.category} onChange={e => upd("category", e.target.value)}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></label>
        <label>Yield / servings per batch<input type="number" min="1" value={form.yield_servings} onChange={e => upd("yield_servings", Number(e.target.value))} data-testid="recipe-yield-input"/></label>
        <label>Selling price (₹ per serving)<input type="number" step="0.01" value={form.selling_price} onChange={e => upd("selling_price", Number(e.target.value))} required data-testid="recipe-price-input"/></label>
        <label>Wastage %<input type="number" step="0.1" value={form.wastage} onChange={e => upd("wastage", Number(e.target.value))}/></label>
        <label>GST %<select value={form.gst_rate} onChange={e => upd("gst_rate", Number(e.target.value))}>{GST_RATES.map(r => <option key={r} value={r}>{`${r}%`}</option>)}</select></label>
        <label>HSN / SAC<input value={form.hsn_sac} onChange={e => upd("hsn_sac", e.target.value)}/></label>
      </div>

      <div className="recipe-ing-head">
        <span className="overline">INGREDIENTS USED PER BATCH</span>
        <button type="button" className="secondary-button" onClick={addIng} data-testid="add-ingredient-row"><Plus size={13}/> Add ingredient</button>
      </div>
      {form.ingredients.length === 0 && <div className="empty small">No ingredients yet. Add some from your inventory to compute cost per plate.</div>}
      <div className="recipe-ing-table">
        {form.ingredients.map((it, i) => (
          <div className="recipe-ing-row" key={i} data-testid={`ingredient-row-${i}`}>
            <select value={it.ingredient_id} onChange={e => pickIngredient(i, e.target.value)} data-testid={`ingredient-picker-${i}`}>
              <option value="">— Pick from inventory —</option>
              {inventory.map(x => <option key={x.id} value={x.id}>{x.name} ({x.unit}) — {money(x.unit_cost)}/{x.unit}</option>)}
              <option value="__custom__">+ Custom ingredient…</option>
            </select>
            <input placeholder="Name" value={it.name} onChange={e => updIng(i, { name: e.target.value })} data-testid={`ingredient-name-${i}`}/>
            <input type="number" step="0.001" placeholder="Qty" value={it.qty} onChange={e => updIng(i, { qty: Number(e.target.value) })} data-testid={`ingredient-qty-${i}`}/>
            <select value={it.unit} onChange={e => updIng(i, { unit: e.target.value })}>{UNITS.map(u => <option key={u}>{u}</option>)}</select>
            <input type="number" step="0.001" placeholder="Rate" value={it.unit_cost} onChange={e => updIng(i, { unit_cost: Number(e.target.value) })} title={`Cost per ${it.unit}`} data-testid={`ingredient-rate-${i}`}/>
            <span className="ing-line-cost">{money(Number(it.qty) * Number(it.unit_cost))}</span>
            <button type="button" className="icon-button" onClick={() => rmIng(i)} data-testid={`remove-ingredient-${i}`}><X size={14}/></button>
          </div>
        ))}
      </div>

      <div className="recipe-costing-summary">
        <div><small>Ingredient subtotal</small><b>{money(subtotal)}</b></div>
        <div><small>+ Wastage ({form.wastage}%)</small><b>{money(costPerBatch - subtotal)}</b></div>
        <div><small>Cost per batch</small><b>{money(costPerBatch)}</b></div>
        <div className="hilight"><small>Cost per serving</small><b data-testid="cost-per-serving">{money(costPerServing)}</b></div>
        <div className={foodCostPct && foodCostPct < 30 ? "good" : "warn"}><small>Food cost %</small><b data-testid="food-cost-pct">{foodCostPct.toFixed(1)}%</b></div>
        <div><small>Margin / serving</small><b>{money(Math.max(0, Number(form.selling_price) - costPerServing))}</b></div>
      </div>

      <label>Prep notes<textarea rows={2} value={form.notes} onChange={e => upd("notes", e.target.value)} placeholder="Optional prep instructions"/></label>

      {error && <div className="auth-alert error">{error}</div>}
      <div className="setup-actions" style={{marginTop:14}}>
        <button type="button" className="secondary-button" onClick={onCancel}>Cancel</button>
        <span style={{flex:1}}/>
        <button className="primary-button" disabled={busy || !form.name.trim()} data-testid="save-recipe-button">{busy ? <Loader2 className="spin" size={14}/> : <Check size={14}/>} Save recipe</button>
      </div>
    </form>
  );
}

export function RecipeModal({ open, onClose, initial, inventory, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const save = async (form) => {
    setBusy(true); setError("");
    try {
      const payload = {
        name: form.name, category: form.category,
        yield_servings: Number(form.yield_servings) || 1,
        selling_price: Number(form.selling_price) || 0,
        wastage: Number(form.wastage) || 0,
        gst_rate: Number(form.gst_rate) || 5,
        hsn_sac: form.hsn_sac || "2106",
        notes: form.notes || "",
        ingredients: form.ingredients.map(i => ({
          ingredient_id: i.ingredient_id || "",
          name: i.name, unit: i.unit, qty: Number(i.qty) || 0, unit_cost: Number(i.unit_cost) || 0,
        })),
      };
      if (initial?.id) {
        const { data } = await api.put(`/recipes/${initial.id}`, payload);
        onSaved?.(data);
      } else {
        const branch = new URLSearchParams(window.location.search).get("branch") || window.__fd_branch || "downtown";
        const { data } = await api.post(`/recipes?branch=${window.__fd_branch || branch}`, payload);
        onSaved?.(data);
      }
      onClose();
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal recipe-modal" data-testid="recipe-modal">
        <div className="modal-head">
          <div><span className="overline">RECIPE &amp; COSTING</span><h2>{initial?.id ? "Edit dish" : "Create dish"}</h2></div>
          <button className="icon-button" onClick={onClose}><X size={18}/></button>
        </div>
        <RecipeForm initial={initial} inventory={inventory} onCancel={onClose} onSave={save} busy={busy} error={error}/>
      </div>
    </div>
  );
}

export default function RecipesView({ recipes, inventory, branch, onChanged }) {
  const [modal, setModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  // Make branch available to the modal
  window.__fd_branch = branch;

  const grouped = useMemo(() => {
    const g = {};
    for (const r of recipes) { (g[r.category] = g[r.category] || []).push(r); }
    return g;
  }, [recipes]);

  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/recipes/${confirmDelete.id}`); setConfirmDelete(null); onChanged?.(); }
    catch (e) { alert(errorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="recipes-head">
        <div><span className="overline">RECIPE MASTER</span><h2 style={{fontSize:20,margin:"6px 0 2px"}}>Dish recipes &amp; costing</h2><p style={{margin:0,color:"var(--muted)",fontSize:13}}>Set the ingredients used per batch — FynDine computes the cost per plate and food-cost percentage.</p></div>
        <button className="primary-button" onClick={() => setModal({ open: true, initial: null })} data-testid="add-recipe-button"><Plus size={16}/> New dish</button>
      </div>

      {Object.keys(grouped).length === 0 && <div className="panel" style={{padding:36,textAlign:"center",color:"var(--muted)"}} data-testid="recipes-empty">No dishes yet. Create one and add the ingredients used per batch — e.g. a plate of Paneer Butter Masala with paneer, tomato, oil, spices.</div>}

      {Object.entries(grouped).map(([cat, list]) => (
        <section key={cat} style={{marginBottom:18}}>
          <span className="overline" style={{display:"block",margin:"8px 0 10px"}}>{cat}</span>
          <div className="recipe-grid">
            {list.map(r => {
              const cps = r.cost / Math.max(1, r.yield_servings);
              const pct = r.selling_price > 0 ? (cps / r.selling_price) * 100 : 0;
              return (
                <article className="recipe-card detailed" key={r.id} data-testid={`recipe-card-${r.id}`}>
                  <div className="recipe-top">
                    <div><ChefHat size={14} color="#f0a768"/> <span className="category-label">{r.category}</span></div>
                    <div style={{display:"flex",gap:4}}>
                      <button className="icon-button" title="Edit" onClick={() => setModal({ open: true, initial: r })} data-testid={`edit-recipe-${r.id}`}><Pencil size={14}/></button>
                      <button className="icon-button" title="Delete" onClick={() => setConfirmDelete(r)} data-testid={`delete-recipe-${r.id}`}><Trash2 size={14}/></button>
                    </div>
                  </div>
                  <h3>{r.name}</h3>
                  <span className="muted-label">Yields {r.yield_servings} · Wastage {r.wastage}% · GST {r.gst_rate ?? 5}%</span>

                  <div className="recipe-ing-list">
                    {(r.ingredients || []).length === 0 && <div className="empty small">No ingredients mapped yet. Click Edit to add them.</div>}
                    {(r.ingredients || []).map((it, i) => (
                      <div key={i} className="recipe-ing-item">
                        <span>{it.name}</span>
                        <small>{it.qty} {it.unit}</small>
                        <b>{money(Number(it.qty) * Number(it.unit_cost))}</b>
                      </div>
                    ))}
                  </div>

                  <div className="cost-line">
                    <div><small>Cost / serving</small><strong>{money(cps)}</strong></div>
                    <div><small>Food cost</small><strong className={pct < 30 ? "green-text" : "orange-text"}>{pct.toFixed(1)}%</strong></div>
                    <div><small>Selling price</small><strong>{money(r.selling_price)}</strong></div>
                  </div>
                  <div className="recipe-foot"><span>Batch cost <b>{money(r.cost)}</b></span><span className="margin">Margin {money(Math.max(0, r.selling_price - cps))}</span></div>
                </article>
              );
            })}
          </div>
        </section>
      ))}

      <RecipeModal
        open={!!modal}
        initial={modal?.initial}
        inventory={inventory}
        onClose={() => setModal(null)}
        onSaved={() => onChanged?.()}
      />

      {confirmDelete && (
        <div className="modal-backdrop">
          <div className="modal" data-testid="recipe-delete-modal">
            <div className="modal-head"><div><span className="overline">CONFIRM</span><h2>Delete this dish?</h2></div><button className="icon-button" onClick={() => setConfirmDelete(null)}><X size={18}/></button></div>
            <p style={{color:"var(--muted)",fontSize:13}}>“{confirmDelete.name}” will be removed from the menu and POS. This cannot be undone.</p>
            <div className="setup-actions" style={{marginTop:14}}>
              <button className="secondary-button" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <span style={{flex:1}}/>
              <button className="primary-button" style={{background:"#8a2a2a"}} disabled={busy} onClick={doDelete} data-testid="confirm-delete-recipe"><Trash2 size={14}/> Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
